import type { PageGraphSource } from './canvasTypes'
import type { GraphNode } from './graphTypes'
import type {
  IsolationNodeSummary,
  PageIsolationSelection,
} from './IsolationControllerContext'
import { buildNodeTooltip } from './nodeTooltip'

export type IsolationStop = { pageId: string; node: GraphNode }
export type SelectedIsolationStop = IsolationStop & { badge: string }

function suppliedBadge(
  entries: PageIsolationSelection[],
  nodeId: string
): string | undefined {
  return entries.map(({ badges }) => badges?.[nodeId]).find(Boolean)
}

export function selectedStops(
  selection: PageIsolationSelection[],
  pageGraphs: PageGraphSource[]
): SelectedIsolationStop[] {
  return pageGraphs.flatMap(({ graph }) => {
    const entries = selection.filter(({ pageId }) => pageId === graph.pageId)
    const nodeIds = new Set(entries.flatMap(({ nodeIds }) => nodeIds))
    const nodes = [...nodeIds].flatMap((id) => graph.getNode(id) ?? [])
    return nodes.map((node, index) => ({
      pageId: graph.pageId,
      node,
      badge: suppliedBadge(entries, node.id) ?? String(index + 1),
    }))
  })
}

export function nodesOnPages(
  nodeIds: string[],
  pageGraphs: PageGraphSource[]
): { pageId: string; nodeId: string }[] {
  return pageGraphs.flatMap(({ graph }) =>
    [...new Set(nodeIds)]
      .filter((nodeId) => graph.getNode(nodeId))
      .map((nodeId) => ({ pageId: graph.pageId, nodeId }))
  )
}

export function findIsolationStop(
  pageGraphs: PageGraphSource[],
  nodeId: string
): IsolationStop | undefined {
  for (const { graph } of pageGraphs) {
    const node = graph.getNode(nodeId)
    if (node) return { pageId: graph.pageId, node }
  }
  return undefined
}

export function summarizeIsolationNode({
  pageId,
  node,
}: IsolationStop): IsolationNodeSummary {
  const { title, subtitle } = buildNodeTooltip(node)
  return { pageId, nodeId: node.id, label: title, subtitle }
}
